import styles from './scheduleHeader.module.scss'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Popover, Skeleton, Spin, Tooltip } from 'antd'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar-date.svg'
import { ReactComponent as PrevIcon } from 'assets/icons/left.svg'
import { ReactComponent as NextIcon } from 'assets/icons/right.svg'
import { Calendar, type CalendarRef } from 'common/components/Calendar/Calendar'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { type ReactNode } from 'react'
import type { ScheduleMonth } from 'features/Schedule/api/schedule.types'
import type { INewLeaseDays } from 'features/Booking/interfaces/ILeaseInfoRoom'
import { useScreenWidth } from 'app/hooks/useScreenWidth'

const DATE_FORMAT = 'YYYY-MM-DD'

type Props = {
  isLoading?: boolean
  isFetching?: boolean
  defaultActiveDate: string | null
  timeZone?: string
  availableDays?: string[]
  scheduleData?: ScheduleMonth
  allowDisabled?: boolean
  forceEnableFutureDate?: boolean
  forceEnablePastDate?: boolean
  minDate?: Dayjs
  maxDate?: Dayjs
  additionalControls?: ReactNode
  isWeekView?: boolean
  onDateChange?: (date: string) => void
  handleClickDisabledDate?: (date: string) => void
  onChangeMonth?: (date: Dayjs) => void
  onChangeWeek?: (date: Dayjs) => void
  onPrevButtonClick?: () => void
  onNextButtonClick?: () => void
  onOpenChange?: (open: boolean) => void
}

const ScheduleHeaderCalendar = ({
  isLoading,
  isFetching = false,
  allowDisabled = false,
  defaultActiveDate = dayjs().format(DATE_FORMAT),
  availableDays = [],
  scheduleData = {},
  minDate,
  maxDate,
  forceEnableFutureDate = false,
  forceEnablePastDate = false,
  additionalControls,
  isWeekView = false,
  onDateChange = () => {},
  handleClickDisabledDate = () => {},
  onChangeMonth = () => {},
  onChangeWeek = () => {},
  onPrevButtonClick = () => {},
  onNextButtonClick = () => {},
  onOpenChange = () => {},
}: Props) => {
  const [activeDate, setActiveDate] = useState<string>(defaultActiveDate)
  const [startInterval, setStartInterval] = useState<string>(defaultActiveDate)
  const [isPopoverVisible, setIsPopoverVisible] = useState<boolean>(false)
  const calendarRef = useRef<CalendarRef>(null)
  const { isMobile } = useScreenWidth()

  useEffect(() => {
    if (defaultActiveDate) {
      setActiveDate(defaultActiveDate)
      setStartInterval(defaultActiveDate)
    }
  }, [defaultActiveDate])

  const startOfCurrentWeek = dayjs(startInterval).startOf('week')
  const currentDate = dayjs(startInterval)

  const isPrevDisabled = isWeekView
    ? !forceEnablePastDate && startOfCurrentWeek.isBefore(availableDays?.[0])
    : !forceEnablePastDate && currentDate.subtract(1, 'day').isBefore(availableDays?.[0])

  const isNextDisabled = isWeekView
    ? !forceEnableFutureDate && startOfCurrentWeek.add(7, 'days').isAfter(availableDays?.at(-1))
    : !forceEnableFutureDate && currentDate.add(1, 'day').isAfter(availableDays?.at(-1))

  const handlePressArrow = (isNext = true) => {
    const daysToAdd = isWeekView ? 7 : 1
    const nextDate = dayjs(startInterval)
      .add(daysToAdd * (isNext ? 1 : -1), 'days')
      .format(DATE_FORMAT)

    const currentWeekStart = dayjs(startInterval).startOf('week')
    const nextWeekStart = dayjs(nextDate).startOf('week')

    setStartInterval(nextDate)
    setActiveDate(nextDate)
    onDateChange(nextDate)

    if (!currentWeekStart.isSame(nextWeekStart, 'day')) onChangeWeek(dayjs(nextDate))

    const currentMonth = dayjs(startInterval).format('MMM')
    const nextMonth = dayjs(nextDate).format('MMM')
    if (currentMonth !== nextMonth) onChangeMonth(dayjs(nextDate).startOf('month'))
  }

  const onChangeDate = (date: Dayjs) => {
    const newData = date.format(DATE_FORMAT)
    setActiveDate(newData)
    onDateChange(newData)
  }

  const checkDisabledDate = (date: Dayjs) => {
    if (allowDisabled) return false

    return !availableDays.includes(date.format(DATE_FORMAT))
  }

  const handleDateClick = (date: Dayjs) => {
    // old condition to prevent changing the month
    // const calendarRefDate = calendarRef?.current?.getCurrentDate()
    // if (calendarRefDate && !date.isSame(calendarRefDate, 'month')) return
    if (!checkDisabledDate(date)) {
      onChangeDate(date)
      setIsPopoverVisible(false)
      onOpenChange(false)
      return
    }

    handleClickDisabledDate(date.format(DATE_FORMAT))
  }

  const convertScheduleDataToLeaseDays = (data: ScheduleMonth): INewLeaseDays => {
    const convertedData: INewLeaseDays = {}

    Object.keys(data).forEach((dateKey) => {
      convertedData[dateKey] = {
        active: data[dateKey].active,
      }
    })

    return convertedData
  }

  const calendarPopover = (
    <Calendar
      ref={calendarRef}
      onPrevButtonClick={onPrevButtonClick}
      onNextButtonClick={onNextButtonClick}
      handleChangeMonth={(_, month) => {
        if (month) onChangeMonth(dayjs(month).startOf('month'))
      }}
      handleChangeDate={(d) => handleDateClick(dayjs(d, DATE_FORMAT))}
      triggerInitialEffect={false}
      days={convertScheduleDataToLeaseDays(scheduleData)}
      currentDay={defaultActiveDate}
      selectedDays={availableDays}
      minDate={minDate}
      maxDate={maxDate}
      isStrictDisable
      // needConfirm
    />
  )

  const displayDate = useMemo(() => {
    if (isWeekView) {
      const weekStart = dayjs(activeDate).startOf('week')
      const weekEnd = dayjs(activeDate).endOf('week')
      return `${weekStart.format('D MMM YYYY')} - ${weekEnd.format('D MMM YYYY')}`
    }
    const mobileFormat = isMobile ? 'ddd, MMM D, YYYY' : 'dddd, MMMM D, YYYY'
    return dayjs(activeDate).format(mobileFormat)
  }, [activeDate, isWeekView])

  if (isLoading) return <Skeleton.Node className={styles.skeleton} active />

  return (
    <Spin spinning={isFetching}>
      <div className={styles.container}>
        <div className={styles.mainContainer}>
          <div className={styles.content}>
            <Tooltip title={isWeekView ? 'Previous week' : 'Previous day'}>
              <button
                disabled={isPrevDisabled}
                className={styles.actionButton}
                onClick={() => handlePressArrow(false)}>
                <PrevIcon />
              </button>
            </Tooltip>

            <div className={styles.currentDate}>{displayDate}</div>
            <Popover
              content={calendarPopover}
              rootClassName={styles.popoverContainer}
              destroyOnHidden
              placement={isMobile ? 'bottom' : 'bottomLeft'}
              trigger='click'
              open={isPopoverVisible}
              onOpenChange={(visible) => {
                setIsPopoverVisible(visible)
                onOpenChange(visible)
              }}>
              <button className={styles.calendarIcon}>
                <CalendarIcon />
              </button>
            </Popover>

            <Tooltip title={isWeekView ? 'Next week' : 'Next day'}>
              <button
                disabled={isNextDisabled}
                className={styles.actionButton}
                onClick={() => handlePressArrow()}>
                <NextIcon />
              </button>
            </Tooltip>
          </div>
          {additionalControls && (
            <div className={styles.additionalControls}>{additionalControls}</div>
          )}
        </div>
      </div>
    </Spin>
  )
}

export default ScheduleHeaderCalendar
